import { useCallback, useMemo, useRef, useState } from "react";
import { getTodayString } from "@src/packages/utils/date";
import type { ComponentRef } from "react";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  TextInput,
} from "react-native";
import type { LayoutChangeEvent } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  Pill,
  ClipboardList,
  ShieldAlert,
  Globe,
  BookOpen,
  ExternalLink,
  Pencil,
  RefreshCw,
  Trash2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileText,
  History,
  Hash,
  AlertCircle,
  AlertTriangle,
  Syringe,
  PlayCircle,
  Venus,
  Mars,
  VenusAndMars,
  Snowflake,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import type {
  ContentBlock,
  ContentBlockId,
  MedicationBody,
  MedicationMarketContext,
} from "@src/packages/content";
import {
  getBlock,
  InteractionsList,
  resolveMedicationResources,
} from "@src/packages/content";
import {
  IconButton,
  useScrollHaptics,
  springs,
  SectionTitle,
  Collapsible,
} from "@louise/moon-ui";
import { useCountryCode, useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { isProviderOwnedMedication } from "@src/packages/care/medication-ownership";
import { useMedicationLogs } from "@src/packages/care/hooks/useMedicationLogs";
import type { MedicationLog } from "@src/packages/care/hooks/useMedicationLogs";
import { useMedications } from "@src/packages/care/hooks/useMedications";
import type { Medication } from "@src/packages/care/hooks/useMedications";
import { useUpdateMedication, useDeleteMedication } from "@src/packages/care";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  isMedicationStopped,
  parseStopReason,
  parseStoppedNoteBody,
  prependStoppedMedicationNote,
} from "@src/packages/care/medications";
import {
  MedicationRow,
  type MedicationForm,
} from "@src/packages/homepage/plan/components/MedicationRow";
import { MedicationVideoEmbed } from "./MedicationVideoEmbed";
import { parseYouTubeId } from "./medicationVideo";
import { flagFromIso } from "./countryFlag";
import { SideEffectTags } from "./SideEffectTags";
import LinkedContentSheet from "./LinkedContentSheet";
import EvidenceLevelChip, { EVIDENCE_LEVELS } from "./EvidenceLevelChip";
import EvidenceLevelModal from "./EvidenceLevelModal";
import { FactSheetBadge } from "./FactSheetHeader";

const pillCapsuleImage =
  require("../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../assets/images/injection-pen.png") as number;
const COPPER = "#B84A16";

/**
 * Section title for the factsheet. Wraps the shared `SectionTitle` in the
 * standard `marginBottom: 10` spacer the rest of the app uses (see
 * CycleProtocolSection / ComingUpSection) so titles don't hug their content.
 */
function FactsheetSectionTitle({
  icon,
  children,
}: {
  icon?: LucideIcon;
  children: string;
}) {
  return (
    <View style={{ marginBottom: 10 }}>
      <SectionTitle icon={icon}>{children}</SectionTitle>
    </View>
  );
}

/**
 * Injection-specific tips shown inside "How to take" for injectable
 * medications. Surfaces the practical "how do I avoid bruising / pain" advice
 * patients most often ask about, route-aware (subcutaneous vs intramuscular).
 * Reuses the shared guide_* tip strings (translated en + fr).
 */
function InjectionTips({
  subcutaneous,
  intramuscular,
}: {
  subcutaneous: boolean;
  intramuscular: boolean;
}) {
  const t = useTranslations();
  if (!subcutaneous && !intramuscular) return null;

  const tips: string[] = [];
  if (subcutaneous) {
    tips.push(
      t("medication_factsheet.guide_subq_tip_1"),
      t("medication_factsheet.guide_subq_tip_2"),
      t("medication_factsheet.guide_subq_tip_3"),
      t("medication_factsheet.guide_subq_tip_4"),
    );
  }
  if (intramuscular) {
    tips.push(
      t("medication_factsheet.guide_im_tip_1"),
      t("medication_factsheet.guide_im_tip_3"),
      t("medication_factsheet.guide_im_tip_4"),
      t("medication_factsheet.injection_tip_im_help"),
    );
  }

  return (
    <View style={{ marginTop: 18 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          marginBottom: 8,
        }}
      >
        {t("medication_factsheet.injection_tips_heading")}
      </Text>
      {tips.map((tip, i) => (
        <View
          key={i}
          style={{
            flexDirection: "row",
            gap: 8,
            marginBottom: 6,
            alignItems: "flex-start",
          }}
        >
          <Text
            style={{
              fontSize: 14,
              color: EMBER.colors.warm,
              lineHeight: 22,
            }}
          >
            {"•"}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textMid,
              lineHeight: 22,
              flex: 1,
            }}
          >
            {tip}
          </Text>
        </View>
      ))}
    </View>
  );
}

function hasPastMedicationEndDate(endDate: string | null | undefined): boolean {
  if (!endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(`${endDate}T00:00:00`);
  return end.getTime() < today.getTime();
}

function getMedImage(body: MedicationBody): number {
  const forms = body.commonForms;
  if (forms.includes("pen")) return injectionPenImage;
  if (forms.includes("pre_filled_syringe")) return vialSyringeImage;
  if (forms.includes("vial")) return vialSyringeImage;
  return pillCapsuleImage;
}

const ROUTE_KEYS: Record<string, string> = {
  subcutaneous: "medication_factsheet.route_subcutaneous",
  intramuscular: "medication_factsheet.route_intramuscular",
  oral: "medication_factsheet.route_oral",
  vaginal: "medication_factsheet.route_vaginal",
  intravaginal: "medication_factsheet.route_vaginal",
  topical: "medication_factsheet.route_topical",
  nasal: "medication_factsheet.route_nasal",
  intranasal: "medication_factsheet.route_nasal",
  sublingual: "medication_factsheet.route_sublingual",
  transdermal: "medication_factsheet.route_transdermal",
  rectal: "medication_factsheet.route_rectal",
  intravenous: "medication_factsheet.route_intravenous",
};

function formatRoute(route: string, t: (key: string) => string): string {
  const key = ROUTE_KEYS[route];
  return (
    (key ? t(key) : null) ??
    route.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function needsRefrigeration(storage?: string): boolean {
  if (!storage) return false;
  const normalized = storage.toLowerCase();
  if (
    normalized.includes("do not refrigerate") ||
    normalized.includes("ne pas réfrigérer") ||
    normalized.includes("ne pas refrigerer") ||
    normalized.includes("some products may require refrigeration") ||
    normalized.includes("some compounded") ||
    normalized.includes("storage depends") ||
    normalized.includes("for best freshness")
  ) {
    return false;
  }
  return (
    normalized.includes("store in the refrigerator") ||
    normalized.includes("store unopened in the refrigerator") ||
    normalized.includes("store unopened pens in the refrigerator") ||
    normalized.includes("store unopened cartridges in the refrigerator") ||
    normalized.includes("keep refrigerated") ||
    normalized.includes("must be refrigerated") ||
    normalized.includes("stored in the refrigerator") ||
    normalized.includes("keep in the fridge") ||
    normalized.includes("store in the fridge") ||
    normalized.includes("store in a fridge") ||
    normalized.includes("conserver au réfrigérateur") ||
    normalized.includes("à conserver au réfrigérateur") ||
    normalized.includes("au réfrigérateur (2-8") ||
    normalized.includes("au refrigerateur (2-8") ||
    normalized.includes("refrigerated at 2-8") ||
    normalized.includes("refrigerated. hcg")
  );
}

/** Map API form value to content MedicationForm key for howToTakeByForm lookup */
const API_FORM_TO_CONTENT_FORM: Record<string, string> = {
  pre_filled_pen: "pen",
  pre_filled_syringe: "pre_filled_syringe",
  injection_solution: "vial",
  lyophilized_powder: "lyophilized_powder",
  multi_vial: "multi_vial",
  other: "vial",
};

const DELIVERY_METHOD_KEYS: Record<string, string> = {
  pre_filled_syringe: "medication_factsheet.delivery_pre_filled_syringe",
  pre_filled_pen: "medication_factsheet.delivery_pre_filled_pen",
  injection_solution: "medication_factsheet.delivery_injection_solution",
  lyophilized_powder: "medication_factsheet.delivery_lyophilized_powder",
  multi_vial: "medication_factsheet.delivery_multi_vial",
};

/** User-specific context that can be passed from cycle detail */
export type MedicationUserContext = {
  /** The brand name the user originally added (e.g., "Ovaleap" instead of content block title "Gonal-F") */
  originalName?: string;
  dosage?: string;
  route?: string | null;
  /** Selected delivery form (e.g., "pre_filled_pen", "injection_solution") for form-specific instructions */
  selectedForm?: string;
  /** Scheduled administration times (e.g., ["08:00", "18:00"]) */
  timeTaken?: string[];
  /** Medication start date (YYYY-MM-DD) */
  startDate?: string;
  /** Medication end date (YYYY-MM-DD) or null for ongoing */
  endDate?: string | null;
  /** User notes */
  notes?: string;
  /**
   * Record ownership from the API (`source`). Provider/system-owned records
   * render read-only: edit/stop stay hidden with an explanation (#2222).
   */
  source?: string | null;
  sinceDay?: string;
  history?: {
    cycle: string;
    dosage: string;
    outcome?: string;
    note?: string;
  }[];
  prescription?: {
    prescriber: string;
    pharmacy?: string;
    rxNumber?: string;
    refillsRemaining?: number;
    prescribedDate?: string;
    quantity?: string;
  };
};

type Props = {
  item: ContentBlock<"medication"> | null;
  onClose: () => void;
  userContext?: MedicationUserContext;
  /** Patient medication ID — enables inline Stop / Delete actions */
  medicationId?: string;
  /** Opens the shared taken/late/missed dose logger for this occurrence. */
  onLogDosePress?: () => void;
  /** Called when the user taps "Edit". When provided, opens the external add-medication modal (pre-filled) rather than an inline dose form. */
  onEditPress?: () => void;
  /** Display name when no content-db factsheet exists. Lets the sheet still
   * open with the patient context for off-catalog medications. */
  fallbackName?: string;
  /** Optional care-country override; language is never used as market data. */
  marketContext?: MedicationMarketContext;
};

type MedicationInstructionStep = {
  title: string;
  body?: string;
  warning: boolean;
};

function parseMedicationInstructionText(
  text: string,
): MedicationInstructionStep[] {
  const rawSteps = text
    .split(" | ")
    .map((s) => s.trim())
    .filter(Boolean);

  return rawSteps.map((step) => {
    const warning = step.startsWith("⚠");
    const cleaned = step
      .replace(/^⚠\s*/, "")
      .replace(/^Step\s+\d+:\s*/i, "")
      .trim();
    const match = cleaned.match(/^(.+?)\s+-\s+(.+)$/);

    if (match?.[1] && match[2]) {
      return { title: match[1].trim(), body: match[2].trim(), warning };
    }

    return { title: cleaned, warning };
  });
}

function MedicationInstructionRows({
  steps,
}: {
  steps: MedicationInstructionStep[];
}) {
  if (steps.length === 0) return null;

  const onlyStep = steps[0];

  if (steps.length === 1 && onlyStep && !onlyStep.body) {
    return (
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {onlyStep.title}
      </Text>
    );
  }

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.28)",
        overflow: "hidden",
      }}
    >
      {steps.map((step, i) => (
        <View
          key={`${step.title}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88, 78, 68, 0.10)",
              marginTop: 1,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              {i + 1}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.textMid,
                lineHeight: 18,
                marginBottom: step.body ? 3 : 0,
              }}
            >
              {step.title}
            </Text>
            {step.body ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {step.body}
              </Text>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
}

function MedicationGoodToKnowRows({
  steps,
}: {
  steps: MedicationInstructionStep[];
}) {
  if (steps.length === 0) return null;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.28)",
        overflow: "hidden",
      }}
    >
      {steps.map((step, i) => (
        <View
          key={`${step.title}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(190,88,57,0.10)",
              marginTop: 1,
            }}
          >
            <AlertCircle size={13} color={EMBER.colors.coral} strokeWidth={2} />
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.coral,
                lineHeight: 18,
                marginBottom: step.body ? 3 : 0,
              }}
            >
              {step.title}
            </Text>
            {step.body ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {step.body}
              </Text>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
}

function HowToTakeSteps({ text }: { text: string }) {
  const steps = parseMedicationInstructionText(text).filter(
    (step) => !step.warning,
  );

  return <MedicationInstructionRows steps={steps} />;
}

function GoodToKnowSteps({ text }: { text: string }) {
  const steps = parseMedicationInstructionText(text).filter(
    (step) => step.warning,
  );

  return <MedicationGoodToKnowRows steps={steps} />;
}

/**
 * Safely call user-context hooks.
 * If the hooks throw (e.g. no auth context), we return empty data.
 */
function useUserMedicationContext(itemTitle: string) {
  let logsData: MedicationLog[] | undefined;
  let medsData: Medication[] | undefined;

  try {
    const logsQuery = useMedicationLogs();
    logsData = logsQuery.data;
  } catch {
    // No auth context — skip
  }

  try {
    const medsQuery = useMedications();
    medsData = medsQuery.data;
  } catch {
    // No auth context — skip
  }

  const logs = useMemo(() => logsData ?? [], [logsData]);
  const medications = useMemo(() => medsData ?? [], [medsData]);

  const matchingLogs = useMemo(() => {
    if (logs.length === 0) return [];
    const lowerTitle = itemTitle.toLowerCase();
    return logs.filter(
      (log) => log.medicationName.toLowerCase().indexOf(lowerTitle) !== -1,
    );
  }, [logs, itemTitle]);

  const currentPrescription = useMemo(() => {
    if (medications.length === 0) return null;
    const lowerTitle = itemTitle.toLowerCase();
    return (
      medications.find(
        (m) =>
          m.name?.toLowerCase() === lowerTitle ||
          m.molecule?.toLowerCase() === lowerTitle,
      ) ?? null
    );
  }, [medications, itemTitle]);

  return { matchingLogs, currentPrescription, medications };
}

function normalizeLinkedName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function blockNamesForMatching(block: ContentBlock): string[] {
  const names = [block.title];
  if (block.kind === "medication") {
    const medicationBlock = block as ContentBlock<"medication">;
    names.push(
      medicationBlock.body.genericName,
      ...(medicationBlock.body.aliases ?? []),
    );
  }
  if (block.kind === "supplement") {
    const supplementBlock = block as ContentBlock<"supplement">;
    names.push(supplementBlock.body.name);
  }
  return names.map(normalizeLinkedName).filter(Boolean);
}

function isBlockInMedicationList(
  block: ContentBlock,
  medications: Medication[],
) {
  const blockNames = blockNamesForMatching(block);
  return medications.some((medication) => {
    const medicationNames = [
      medication.name,
      medication.molecule,
      medication.moleculeType,
    ]
      .filter((value): value is string => Boolean(value))
      .map(normalizeLinkedName);
    return medicationNames.some((medicationName) =>
      blockNames.some(
        (blockName) =>
          medicationName === blockName ||
          medicationName.includes(blockName) ||
          blockName.includes(medicationName),
      ),
    );
  });
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, color: EMBER.colors.textLight }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.text,
          flexShrink: 1,
          textAlign: "right",
          marginLeft: 16,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

/** Format time array into human-readable schedule */
function formatSchedule(
  times: string[],
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  if (times.length === 0) return t("medication_factsheet.schedule_as_needed");
  const formatTime = (raw: string): string => {
    const hour = parseInt(raw.split(":")[0] ?? "0", 10);
    if (hour < 12) return t("medication_factsheet.schedule_morning");
    if (hour < 17) return t("medication_factsheet.schedule_afternoon");
    if (hour < 21) return t("medication_factsheet.schedule_evening");
    return t("medication_factsheet.schedule_night");
  };
  const labels = times.map((raw) => `${formatTime(raw)} (${raw})`);
  return t("medication_factsheet.schedule_daily_template", {
    params: {
      count: String(times.length),
      labels: labels.join(" & "),
    },
  });
}

/** Format date string for display */
function formatDateCompact(dateStr: string, locale: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeContextName(name: string | null | undefined): string {
  return (name ?? "")
    .trim()
    .toLowerCase()
    .replace(/s+/g, " ")
    .replace(/[®™]/g, "");
}

function getDistinctContextName(
  savedName: string | null | undefined,
  displayName: string,
): string | null {
  const trimmed = savedName?.trim();
  if (!trimmed) return null;
  return normalizeContextName(trimmed) === normalizeContextName(displayName)
    ? null
    : trimmed;
}

function inferMedicationRowForm({
  selectedForm,
  route,
  commonForms,
}: {
  selectedForm: string | null | undefined;
  route: string | null | undefined;
  commonForms: MedicationBody["commonForms"];
}): MedicationForm | undefined {
  const normalized = [selectedForm, route, ...commonForms]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (normalized.includes("pen")) return "pen";
  if (
    normalized.includes("vial") ||
    normalized.includes("syringe") ||
    normalized.includes("injection_solution")
  ) {
    return "vial";
  }
  if (normalized.includes("suppository") || normalized.includes("vaginal")) {
    return "suppository";
  }
  if (
    normalized.includes("oral") ||
    normalized.includes("pill") ||
    normalized.includes("tablet") ||
    normalized.includes("capsule")
  ) {
    return "pill";
  }
  return undefined;
}

/** Compute duration label from start/end dates */
function formatDuration(
  startDate: string,
  endDate: string | null | undefined,
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  if (!endDate) return t("medication_factsheet.duration_ongoing");
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  const days = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days <= 1) return t("medication_factsheet.duration_1_day");
  if (days < 7) {
    return t("medication_factsheet.duration_n_days", {
      params: { n: String(days) },
    });
  }
  if (days === 7) return t("medication_factsheet.duration_1_week");
  if (days < 30) {
    return t("medication_factsheet.duration_n_weeks", {
      params: { n: String(Math.round(days / 7)) },
    });
  }
  if (days < 60) return t("medication_factsheet.duration_1_month");
  return t("medication_factsheet.duration_n_months", {
    params: { n: String(Math.round(days / 30)) },
  });
}

function UserMedCard({
  displayName,
  commonForms,
  userContext,
  currentPrescription,
  matchingLogs,
  onEditPress,
}: {
  displayName: string;
  commonForms: MedicationBody["commonForms"];
  userContext: MedicationUserContext | undefined;
  currentPrescription: Medication | null;
  matchingLogs: MedicationLog[];
  onEditPress?: () => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  // Build dosage string from best available source
  const dosage =
    userContext?.dosage ??
    (currentPrescription?.dose != null
      ? `${currentPrescription.dose}${currentPrescription.doseUnit ? ` ${currentPrescription.doseUnit}` : ""}`
      : null) ??
    currentPrescription?.notes ??
    null;

  const { format: fmtDate } = useDateFormat();
  const hasPatientRoute =
    userContext != null &&
    Object.prototype.hasOwnProperty.call(userContext, "route");
  const rawRoute = hasPatientRoute
    ? (userContext.route ?? null)
    : (currentPrescription?.defaultRoute ??
      currentPrescription?.intakeType ??
      null);
  const route = rawRoute ? formatRoute(rawRoute, t) : null;

  const deliveryMethodKey = userContext?.selectedForm
    ? DELIVERY_METHOD_KEYS[userContext.selectedForm]
    : null;
  const deliveryMethod = deliveryMethodKey ? t(deliveryMethodKey) : null;

  const schedule =
    userContext?.timeTaken && userContext.timeTaken.length > 0
      ? formatSchedule(userContext.timeTaken, t)
      : null;

  const startDate = userContext?.startDate
    ? formatDateCompact(userContext.startDate, locale)
    : null;

  const endDate = userContext?.endDate
    ? formatDateCompact(userContext.endDate, locale)
    : null;

  const duration = userContext?.startDate
    ? formatDuration(userContext.startDate, userContext.endDate, t)
    : null;

  const since =
    userContext?.sinceDay ??
    (currentPrescription?.createdAt
      ? fmtDate(currentPrescription.createdAt)
      : null);

  const notes = userContext?.notes ?? null;
  const isStoppedContext = isMedicationStopped(notes);
  const stoppedReason = isStoppedContext ? parseStopReason(notes) : null;
  const isPastContext = hasPastMedicationEndDate(userContext?.endDate);
  const statusLabel = isStoppedContext
    ? stoppedReason
      ? `${t("medication_factsheet.context_status_stopped")} • ${stoppedReason}`
      : t("medication_factsheet.context_status_stopped")
    : isPastContext
      ? t("medication_factsheet.context_status_previous")
      : t("medication_factsheet.context_status_current");
  const patientMedicationName = getDistinctContextName(
    userContext?.originalName ?? currentPrescription?.name,
    displayName,
  );
  const rowName =
    userContext?.originalName?.trim() ||
    currentPrescription?.name?.trim() ||
    displayName;
  const dateDetail =
    isStoppedContext && endDate
      ? t("medication_factsheet.context_stopped_on", {
          params: { date: endDate },
        })
      : isPastContext && endDate
        ? t("medication_factsheet.context_ended_on", {
            params: { date: endDate },
          })
        : startDate
          ? t("medication_factsheet.context_since", {
              params: { date: startDate },
            })
          : since
            ? t("medication_factsheet.context_since", {
                params: { date: since },
              })
            : null;
  const plannedEnd =
    endDate && !isStoppedContext && !isPastContext
      ? t("medication_factsheet.context_ends_on", {
          params: { date: endDate },
        })
      : null;
  const loggedDoseSummary =
    matchingLogs.length > 0
      ? `${t("medication_factsheet.row_doses_logged")}: ${matchingLogs.length}${
          matchingLogs[0] ? ` \u2022 ${fmtDate(matchingLogs[0].takenAt)}` : ""
        }`
      : null;
  const subtitle = [
    isStoppedContext ? null : statusLabel,
    isStoppedContext ? null : dateDetail,
    schedule,
    plannedEnd,
    duration,
    loggedDoseSummary,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" \u00b7 ");
  const format = deliveryMethod ?? route ?? undefined;
  const form = inferMedicationRowForm({
    selectedForm: userContext?.selectedForm,
    route: rawRoute,
    commonForms,
  });
  const displayableNotes = parseStoppedNoteBody(notes);
  return (
    <View testID="patient-medication-summary">
      <MedicationRow
        name={patientMedicationName ?? rowName}
        dose={dosage ?? undefined}
        format={format}
        subtitle={subtitle}
        form={form}
        surface="standalone"
        rightAction={onEditPress ? "chevron" : "none"}
        onPress={onEditPress}
      />
      {displayableNotes ? (
        <View
          testID="patient-medication-notes"
          style={{
            marginTop: 10,
            padding: 12,
            borderRadius: 12,
            backgroundColor: "#FFFFFF",
            borderWidth: 1,
            borderColor: "rgba(111,98,90,0.12)",
            gap: 4,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <FileText
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={2}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "800",
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {t("medication_factsheet.row_notes")}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.text,
            }}
          >
            {displayableNotes}
          </Text>
        </View>
      ) : null}
      {isStoppedContext ? (
        <View
          testID="patient-medication-stopped-banner"
          style={{
            marginTop: 10,
            padding: 12,
            borderRadius: 12,
            backgroundColor: "rgba(220, 38, 38, 0.06)",
            borderWidth: 1,
            borderColor: "rgba(220, 38, 38, 0.22)",
            gap: 6,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <AlertCircle size={14} color="#DC2626" strokeWidth={2} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 13,
                color: "#DC2626",
                fontWeight: "800",
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {t("medication_factsheet.stopped_banner_title")}
            </Text>
          </View>
          {endDate ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 18,
                color: EMBER.colors.text,
              }}
            >
              {t("medication_factsheet.stopped_banner_since", {
                params: { date: endDate },
              })}
            </Text>
          ) : null}
          {stoppedReason ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 18,
                color: EMBER.colors.text,
              }}
            >
              {t("medication_factsheet.stopped_banner_reason", {
                params: { reason: stoppedReason },
              })}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

type MedicationActionTone = "default" | "accent" | "warning";

function MedicationActionRow({
  icon: Icon,
  title,
  description,
  onPress,
  action,
  disabled,
  tone = "default",
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  onPress?: () => void;
  /** Stable analytics identifier, required because `title` is translated. */
  action?: string;
  disabled?: boolean;
  tone?: MedicationActionTone;
}) {
  const capturePress = usePressCapture("MedicationActionRow");
  const palette =
    tone === "accent"
      ? {
          iconColor: COPPER,
          iconBackground: "rgba(184, 74, 22, 0.08)",
          titleColor: EMBER.colors.text,
        }
      : tone === "warning"
        ? {
            iconColor: "#EA580C",
            iconBackground: "rgba(234, 88, 12, 0.08)",
            titleColor: "#EA580C",
          }
        : {
            iconColor: EMBER.colors.textMid,
            iconBackground: "rgba(0,0,0,0.04)",
            titleColor: EMBER.colors.text,
          };

  const rowStyle = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: "rgba(0,0,0,0.02)",
    borderRadius: 12,
  };

  const content = (
    <>
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          backgroundColor: palette.iconBackground,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={14} color={palette.iconColor} strokeWidth={1.5} />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            fontWeight: "600",
            color: palette.titleColor,
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textLight,
            marginTop: 1,
          }}
        >
          {description}
        </Text>
      </View>
      {onPress ? (
        <ChevronRight
          size={14}
          color={EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      ) : null}
    </>
  );

  if (!onPress) {
    return <View style={rowStyle}>{content}</View>;
  }

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => {
        capturePress(pressLabel(title), action);
        onPress();
      }}
      disabled={disabled}
      style={({ pressed }) => ({
        ...rowStyle,
        opacity: disabled ? 0.5 : pressed ? 0.72 : 1,
      })}
    >
      {content}
    </Pressable>
  );
}

type MedicationFactsheetSectionId =
  | "purpose"
  | "howToTake"
  | "videos"
  | "goodToKnow"
  | "sideEffects"
  | "interactions"
  | "storage"
  | "availableAs"
  | "resources"
  | "evidence"
  | "history"
  | "prescription";

type MedicationFactsheetSectionNavItem = {
  id: MedicationFactsheetSectionId;
  label: string;
  icon: LucideIcon;
};

function MedicationFactsheetSectionChips({
  items,
  onPress,
}: {
  items: MedicationFactsheetSectionNavItem[];
  onPress: (id: MedicationFactsheetSectionId) => void;
}) {
  const capturePress = usePressCapture("MedicationFactsheetSectionChips");

  if (items.length < 2) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        gap: 8,
        paddingVertical: 14,
        paddingHorizontal: 2,
      }}
      style={{ marginTop: 4 }}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Pressable
            key={item.id}
            accessibilityRole="button"
            accessibilityLabel={item.label}
            onPress={() => {
              capturePress(
                pressLabel(item.label),
                `medication_section_${item.id}`,
              );
              onPress(item.id);
            }}
            style={({ pressed }) => ({
              flexDirection: "row",
              alignItems: "center",
              gap: 5,
              paddingHorizontal: 14,
              paddingVertical: 7,
              borderRadius: 20,
              backgroundColor: "rgba(255,255,255,0.4)",
              borderWidth: 1,
              borderColor: "rgba(255,255,255,0.5)",
              opacity: pressed ? 0.72 : 1,
            })}
          >
            <Icon size={13} color={EMBER.colors.textMid} strokeWidth={1.5} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "400",
                color: EMBER.colors.textMid,
              }}
            >
              {item.label}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function MedicationDetailContent({
  item,
  onClose,
  userContext,
  skipExitAnimation,
  medicationId,
  onLogDosePress,
  onEditPress,
  marketContext,
}: {
  item: ContentBlock<"medication">;
  onClose: () => void;
  userContext?: MedicationUserContext;
  skipExitAnimation?: boolean;
  medicationId?: string;
  onLogDosePress?: () => void;
  onEditPress?: () => void;
  marketContext?: MedicationMarketContext;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationDetailContent");
  const { data: patient } = usePatient();
  const deviceCountry = useCountryCode();
  const insets = useSafeAreaInsets();
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const onScrollHaptics = useScrollHaptics();
  // "Available as" is collapsed by default to keep the factsheet uncluttered.
  const [brandsExpanded, setBrandsExpanded] = useState(false);
  const scrollRef = useRef<ComponentRef<typeof Animated.ScrollView> | null>(
    null,
  );
  const contentCardOffsetYRef = useRef(0);
  const sectionOffsetsRef = useRef<
    Partial<Record<MedicationFactsheetSectionId, number>>
  >({});

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  const handleContentCardLayout = useCallback((e: LayoutChangeEvent) => {
    contentCardOffsetYRef.current = e.nativeEvent.layout.y;
  }, []);

  const registerSectionLayout = useCallback(
    (sectionId: MedicationFactsheetSectionId) => (e: LayoutChangeEvent) => {
      sectionOffsetsRef.current[sectionId] =
        contentCardOffsetYRef.current + e.nativeEvent.layout.y;
    },
    [],
  );

  const handleSectionNavPress = useCallback(
    (sectionId: MedicationFactsheetSectionId) => {
      const y = sectionOffsetsRef.current[sectionId];
      if (typeof y !== "number") return;
      scrollRef.current?.scrollTo({
        y: Math.max(y - 12, 0),
        animated: true,
      });
    },
    [],
  );

  const body = item.body as MedicationBody;
  const { locale } = useLocale();
  const resolvedMarketContext = useMemo(
    () => ({
      ...marketContext,
      homeCountry:
        marketContext?.homeCountry ?? patient?.addresses?.[0]?.country,
      fallbackCountry: marketContext?.fallbackCountry ?? deviceCountry,
    }),
    [deviceCountry, marketContext, patient?.addresses],
  );
  const fr = locale === "fr" ? body.frBody : undefined;
  const requiresRefrigeration = needsRefrigeration(
    `${body.storage ?? ""} ${body.frBody?.storage ?? ""}`,
  );
  const selectedForm = userContext?.selectedForm;
  const contentFormKey = selectedForm
    ? API_FORM_TO_CONTENT_FORM[selectedForm]
    : undefined;
  const localizedByForm = fr?.howToTakeByForm ?? body.howToTakeByForm;
  const formSpecific =
    contentFormKey && localizedByForm
      ? localizedByForm[contentFormKey as keyof typeof localizedByForm]
      : undefined;
  const howToTakeText = formSpecific ?? fr?.howToTake ?? body.howToTake;
  const goodToKnowSteps = parseMedicationInstructionText(howToTakeText).filter(
    (step) => step.warning,
  );

  // Manufacturer how-to videos: YouTube links embed inline; everything else
  // (Vimeo, manufacturer sites, guides, info) stays a tap-out link row.
  const videoEmbeds = (body.resources ?? []).flatMap((res) => {
    const videoId = res.type === "video" ? parseYouTubeId(res.url) : null;
    return videoId ? [{ videoId, label: res.label, url: res.url }] : [];
  });
  const linkResources = (body.resources ?? []).filter(
    (res) => !(res.type === "video" && parseYouTubeId(res.url)),
  );
  const officialResources = resolveMedicationResources(
    {
      ...body,
      resources: linkResources,
    },
    item.id,
    resolvedMarketContext,
    (brand) =>
      t("medication_factsheet.official_resource_ansm_label", {
        params: { brand },
      }),
  );
  const { matchingLogs, currentPrescription, medications } =
    useUserMedicationContext(item.title);
  const [linkedItem, setLinkedItem] = useState<
    ContentBlock<"medication"> | ContentBlock<"supplement"> | null
  >(null);
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);
  const takingLinkedBlockIds = useMemo(
    () =>
      (body.interactions ?? [])
        .map((interaction) => interaction.linkedBlockId)
        .filter((id): id is string => Boolean(id))
        .filter((id) => {
          const linkedBlock = getBlock(id as ContentBlockId);
          return linkedBlock
            ? isBlockInMedicationList(linkedBlock, medications)
            : false;
        }),
    [body.interactions, medications],
  );
  const handleLinkedBlockPress = useCallback((blockId: string) => {
    const linkedBlock = getBlock(blockId as ContentBlockId);
    if (
      linkedBlock?.kind === "medication" ||
      linkedBlock?.kind === "supplement"
    ) {
      setLinkedItem(
        linkedBlock as ContentBlock<"medication"> | ContentBlock<"supplement">,
      );
    }
  }, []);

  // Merge hook-detected prescription with explicit userContext
  const prescription = userContext?.prescription ?? null;
  const history = userContext?.history ?? null;
  const sideEffects = fr?.sideEffects ?? body.sideEffects;
  const storageText = fr?.storage ?? body.storage;
  const sectionNavigationItems = useMemo(() => {
    const items: MedicationFactsheetSectionNavItem[] = [
      {
        id: "purpose",
        label: t("medication_factsheet.section_purpose"),
        icon: BookOpen,
      },
      {
        id: "howToTake",
        label: t("medication_factsheet.section_how_to_take"),
        icon: ClipboardList,
      },
    ];

    if (videoEmbeds.length) {
      items.push({
        id: "videos",
        label: t("medication_factsheet.section_videos"),
        icon: PlayCircle,
      });
    }
    if (goodToKnowSteps.length) {
      items.push({
        id: "goodToKnow",
        label: t("medication_factsheet.section_good_to_know"),
        icon: AlertCircle,
      });
    }
    if (sideEffects?.length) {
      items.push({
        id: "sideEffects",
        label: t("medication_factsheet.section_side_effects"),
        icon: ShieldAlert,
      });
    }
    if (body.interactions?.length) {
      items.push({
        id: "interactions",
        label: t("medication_factsheet.section_interacts_with"),
        icon: AlertTriangle,
      });
    }
    if (storageText) {
      items.push({
        id: "storage",
        label: t("medication_factsheet.section_storage"),
        icon: AlertCircle,
      });
    }
    if (body.availability?.length || body.brandsByCountry?.length) {
      items.push({
        id: "availableAs",
        label: t("medication_factsheet.section_available_as"),
        icon: Globe,
      });
    }
    if (officialResources.length) {
      items.push({
        id: "resources",
        label: t("medication_factsheet.section_official_resources"),
        icon: BookOpen,
      });
    }
    if (item.sources?.length) {
      items.push({
        id: "evidence",
        label: t("medication_factsheet.section_evidence"),
        icon: BookOpen,
      });
    }
    if (history?.length) {
      items.push({
        id: "history",
        label: t("medication_factsheet.section_your_history"),
        icon: History,
      });
    }
    if (prescription) {
      items.push({
        id: "prescription",
        label: t("medication_factsheet.section_prescription"),
        icon: FileText,
      });
    }

    return items;
  }, [
    body.availability?.length,
    body.brandsByCountry?.length,
    body.interactions?.length,
    goodToKnowSteps.length,
    history?.length,
    item.sources?.length,
    officialResources.length,
    prescription,
    sideEffects?.length,
    storageText,
    t,
    videoEmbeds.length,
  ]);

  // Mutations — only active when medicationId is present
  const updateMedication = useUpdateMedication();
  const deleteMedication = useDeleteMedication();
  const isStopped = isMedicationStopped(userContext?.notes);
  const isMutating = updateMedication.isPending || deleteMedication.isPending;

  // Inline action modes: view = default, stop = reason form. Edit opens the external AddMedicationModal via onEditPress.
  type ActionMode = "view" | "stop";
  const [mode, setMode] = useState<ActionMode>("view");

  const [stopReason, setStopReason] = useState("");
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const openStop = useCallback(() => {
    setStopReason("");
    setMutationError(null);
    setMode("stop");
  }, []);

  const handleConfirmStop = useCallback(() => {
    if (!medicationId) {
      setMutationError(t("medication_factsheet.missing_med_id"));
      return;
    }
    setMutationError(null);
    const reason = stopReason.trim();
    const todayISO = getTodayString();
    updateMedication.mutate(
      {
        medicationId,
        endDate: todayISO,
        notes: prependStoppedMedicationNote(userContext?.notes, reason),
      },
      {
        onSuccess: () => {
          setMode("view");
          handleClose();
        },
        onError: (err) => {
          console.error("[MedicationFactsheet] Stop failed", err);
          setMutationError(
            err instanceof Error
              ? err.message
              : t("medication_factsheet.stop_med_error"),
          );
        },
      },
    );
  }, [
    medicationId,
    stopReason,
    userContext?.notes,
    updateMedication,
    handleClose,
    t,
  ]);

  const handleDelete = useCallback(() => {
    if (!medicationId) return;
    setMutationError(null);
    setDeleteConfirmVisible(true);
  }, [medicationId]);

  const handleConfirmDelete = useCallback(() => {
    if (!medicationId) return;
    deleteMedication.mutate(medicationId, {
      onSuccess: () => {
        setDeleteConfirmVisible(false);
        handleClose();
      },
      onError: (err) => {
        console.error("[MedicationFactsheet] Delete failed", err);
        setMutationError(
          err instanceof Error
            ? err.message
            : t("medication_factsheet.delete_med_error"),
        );
        setDeleteConfirmVisible(false);
      },
    });
  }, [medicationId, deleteMedication, handleClose, t]);

  const providerOwned = isProviderOwnedMedication(userContext?.source);

  const medicationRecordActions =
    medicationId || prescription ? (
      <View
        style={{
          marginTop: 8,
          gap: 8,
        }}
      >
        {mode === "view" && providerOwned && (
          <MedicationActionRow
            icon={ShieldAlert}
            title={t("medication_factsheet.provider_owned_title")}
            description={t("medication_factsheet.provider_owned_note")}
            tone="default"
          />
        )}
        {mode === "view" && (
          <View style={{ gap: 8 }}>
            {medicationId && onEditPress && !providerOwned && (
              <MedicationActionRow
                icon={Pencil}
                title={t("medication_factsheet.action_edit")}
                description={t("medication_factsheet.action_edit_help")}
                onPress={onEditPress}
                action="medication_factsheet.action_edit"
                disabled={isMutating}
                tone="accent"
              />
            )}

            {medicationId && onLogDosePress && (
              <MedicationActionRow
                icon={ClipboardList}
                title={t("medication_factsheet.action_log_dose")}
                description={t("medication_factsheet.action_log_dose_help")}
                onPress={onLogDosePress}
                action="medication_factsheet.action_log_dose"
                disabled={isMutating}
                tone="accent"
              />
            )}

            {prescription && (
              <MedicationActionRow
                icon={RefreshCw}
                title={t("medication_factsheet.action_refill")}
                description={t("medication_factsheet.action_refill_help", {
                  params: {
                    pharmacy: prescription.pharmacy ?? "your pharmacy",
                  },
                })}
              />
            )}

            {medicationId && !isStopped && !providerOwned && (
              <MedicationActionRow
                icon={ShieldAlert}
                title={t("medication_factsheet.action_stop")}
                description={t("medication_factsheet.action_stop_help")}
                onPress={openStop}
                action="medication_factsheet.action_stop"
                disabled={isMutating}
                tone="warning"
              />
            )}
          </View>
        )}

        {mode === "stop" && (
          <View
            style={{
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(234, 88, 12, 0.04)",
              borderWidth: 1,
              borderColor: "rgba(234, 88, 12, 0.15)",
              gap: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: "#EA580C",
              }}
            >
              {t("medication_factsheet.stop_form_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                lineHeight: 18,
              }}
            >
              {t("medication_factsheet.stop_form_help", {
                params: {
                  name: userContext?.originalName ?? item.title,
                },
              })}
            </Text>
            <TextInput
              value={stopReason}
              onChangeText={setStopReason}
              placeholder={t("medication_factsheet.stop_form_placeholder")}
              placeholderTextColor={EMBER.colors.textFaint}
              multiline
              style={{
                borderWidth: 1,
                borderColor: "rgba(234, 88, 12, 0.2)",
                borderRadius: 8,
                paddingHorizontal: 10,
                paddingVertical: 8,
                fontSize: 14,
                color: EMBER.colors.text,
                minHeight: 70,
                textAlignVertical: "top",
                backgroundColor: "#fff",
              }}
            />
            {mutationError && (
              <View
                style={{
                  backgroundColor: "rgba(220, 38, 38, 0.08)",
                  borderWidth: 1,
                  borderColor: "rgba(220, 38, 38, 0.2)",
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: "#991B1B",
                    lineHeight: 18,
                  }}
                >
                  {mutationError}
                </Text>
              </View>
            )}
            <View style={{ flexDirection: "row", gap: 8 }}>
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("medication_factsheet.stop_form_cancel")),
                    "medication_factsheet.stop_form_cancel",
                  );
                  setMode("view");
                }}
                disabled={isMutating}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  alignItems: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    color: EMBER.colors.textMid,
                  }}
                >
                  {t("medication_factsheet.stop_form_cancel")}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("medication_factsheet.stop_form_submit")),
                    "medication_factsheet.stop_form_submit",
                  );
                  handleConfirmStop();
                }}
                disabled={isMutating}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: "#EA580C",
                  alignItems: "center",
                  opacity: isMutating ? 0.6 : 1,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    color: "#fff",
                  }}
                >
                  {updateMedication.isPending
                    ? t("medication_factsheet.stop_form_submitting")
                    : t("medication_factsheet.stop_form_submit")}
                </Text>
              </Pressable>
            </View>
            {medicationId && (
              <Pressable
                accessibilityRole="button"
                accessibilityHint={t("medication_factsheet.action_delete_help")}
                onPress={() => {
                  capturePress(
                    pressLabel(t("medication_factsheet.delete_mistake_action")),
                    "medication_factsheet.delete_mistake_action",
                  );
                  handleDelete();
                }}
                disabled={isMutating}
                style={({ pressed }) => ({
                  alignSelf: "flex-start",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  opacity: isMutating ? 0.5 : pressed ? 0.72 : 1,
                })}
              >
                <Trash2 size={13} color="#DC2626" strokeWidth={1.5} />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    color: "#B91C1C",
                  }}
                >
                  {t("medication_factsheet.delete_mistake_action")}
                </Text>
              </Pressable>
            )}
          </View>
        )}
      </View>
    ) : null;

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            ref={scrollRef}
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              onLayout={handleContentCardLayout}
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedBackgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              {/* Medication illustration */}
              <View
                style={{
                  alignItems: "center",
                  paddingTop: 8,
                  paddingBottom: 20,
                }}
              >
                <Image
                  source={getMedImage(body)}
                  style={{ width: 80, height: 80 }}
                  resizeMode="contain"
                />
              </View>

              {/* Title — centered */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  lineHeight: 30,
                  marginBottom: 4,
                  textAlign: "center",
                }}
              >
                {userContext?.originalName ?? item.title}
              </Text>
              {body.genericName ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textLight,
                    textAlign: "center",
                    marginBottom: 6,
                  }}
                >
                  {fr?.genericName ?? body.genericName}
                </Text>
              ) : null}

              {/* Badge row — centered, matching supplement pattern */}
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 18,
                  marginTop: 10,
                  justifyContent: "center",
                }}
              >
                <FactSheetBadge
                  icon={Pill}
                  label={fr?.drugClass ?? body.drugClass}
                />
                {body.commonRoutes.length > 0 && (
                  <FactSheetBadge
                    icon={Syringe}
                    label={body.commonRoutes
                      .map((r) => formatRoute(r, t))
                      .join(", ")}
                  />
                )}
                {requiresRefrigeration && (
                  <FactSheetBadge
                    icon={Snowflake}
                    label={t("medication_factsheet.badge_keep_refrigerated")}
                    accent
                  />
                )}
                {body.forWhom && (
                  <FactSheetBadge
                    icon={
                      body.forWhom === "both"
                        ? VenusAndMars
                        : body.forWhom === "her"
                          ? Venus
                          : Mars
                    }
                    accessibilityLabel={
                      body.forWhom === "both"
                        ? t("medication_factsheet.partner_both")
                        : body.forWhom === "her"
                          ? t("medication_factsheet.partner_her")
                          : t("medication_factsheet.partner_him")
                    }
                  />
                )}
                {isStopped && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                      paddingHorizontal: 10,
                      paddingVertical: 5,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: `${EMBER.colors.coral}40`,
                      backgroundColor: `${EMBER.colors.coral}18`,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        fontWeight: "600",
                        color: EMBER.colors.coral,
                        letterSpacing: 0.2,
                      }}
                    >
                      {t("medication_factsheet.stopped_badge")}
                    </Text>
                  </View>
                )}
              </View>

              {/* "You're taking this" — from hooks OR userContext */}
              {userContext || currentPrescription ? (
                medicationRecordActions ? (
                  <View
                    style={{
                      borderRadius: 18,
                      borderWidth: 1,
                      borderColor: "rgba(255, 255, 255, 0.78)",
                      backgroundColor: "rgba(255, 255, 255, 0.58)",
                      padding: 14,
                    }}
                  >
                    <UserMedCard
                      displayName={userContext?.originalName ?? item.title}
                      commonForms={body.commonForms}
                      userContext={userContext}
                      currentPrescription={currentPrescription}
                      matchingLogs={matchingLogs}
                      onEditPress={onEditPress}
                    />
                    {medicationRecordActions}
                  </View>
                ) : (
                  <UserMedCard
                    displayName={userContext?.originalName ?? item.title}
                    commonForms={body.commonForms}
                    userContext={userContext}
                    currentPrescription={currentPrescription}
                    matchingLogs={matchingLogs}
                    onEditPress={onEditPress}
                  />
                )
              ) : null}

              {/* "You've taken this before" — only logs, no active prescription */}
              {!userContext &&
              !currentPrescription &&
              matchingLogs.length > 0 ? (
                <UserMedCard
                  displayName={item.title}
                  commonForms={body.commonForms}
                  userContext={undefined}
                  currentPrescription={null}
                  matchingLogs={matchingLogs}
                />
              ) : null}

              {!userContext && !currentPrescription
                ? medicationRecordActions
                : null}

              <MedicationFactsheetSectionChips
                items={sectionNavigationItems}
                onPress={handleSectionNavPress}
              />

              {/* Purpose */}
              <View
                onLayout={registerSectionLayout("purpose")}
                style={{ marginTop: 24 }}
              >
                <FactsheetSectionTitle icon={BookOpen}>
                  {t("medication_factsheet.section_purpose")}
                </FactsheetSectionTitle>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textMid,
                    lineHeight: 21,
                  }}
                >
                  {fr?.purpose ?? body.purpose}
                </Text>
              </View>

              {/* How to take */}
              <View
                onLayout={registerSectionLayout("howToTake")}
                style={{ marginTop: 24 }}
              >
                <FactsheetSectionTitle icon={ClipboardList}>
                  {t("medication_factsheet.section_how_to_take")}
                </FactsheetSectionTitle>
                <HowToTakeSteps text={howToTakeText} />
                <InjectionTips
                  subcutaneous={body.commonRoutes.includes("subcutaneous")}
                  intramuscular={body.commonRoutes.includes("intramuscular")}
                />
              </View>

              {/* How-to videos — inline manufacturer injection/mixing guides */}
              {videoEmbeds.length ? (
                <View
                  onLayout={registerSectionLayout("videos")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={PlayCircle}>
                    {t("medication_factsheet.section_videos")}
                  </FactsheetSectionTitle>
                  <View style={{ gap: 16 }}>
                    {videoEmbeds.map((video) => (
                      <MedicationVideoEmbed
                        key={video.url}
                        videoId={video.videoId}
                        label={video.label}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {goodToKnowSteps.length ? (
                <View
                  onLayout={registerSectionLayout("goodToKnow")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={AlertCircle}>
                    {t("medication_factsheet.section_good_to_know")}
                  </FactsheetSectionTitle>
                  <GoodToKnowSteps text={howToTakeText} />
                </View>
              ) : null}

              {/* Side effects \u2014 tappable pill tags; tap one you're experiencing
                  to log it for today (persists to the symptom tracker). */}
              {sideEffects?.length ? (
                <View
                  onLayout={registerSectionLayout("sideEffects")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={ShieldAlert}>
                    {t("medication_factsheet.section_side_effects")}
                  </FactsheetSectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                      marginBottom: 10,
                    }}
                  >
                    {t("medication_factsheet.side_effects_log_hint")}
                  </Text>
                  <SideEffectTags sideEffects={sideEffects} />
                </View>
              ) : null}

              {/* Interactions with other meds / supplements */}
              {body.interactions?.length ? (
                <View
                  onLayout={registerSectionLayout("interactions")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={AlertTriangle}>
                    {t("medication_factsheet.section_interacts_with")}
                  </FactsheetSectionTitle>
                  <InteractionsList
                    interactions={body.interactions}
                    onLinkedBlockPress={handleLinkedBlockPress}
                    takingLinkedBlockIds={takingLinkedBlockIds}
                    takingLabel={t("medication_factsheet.interaction_taking")}
                    severityLabels={{
                      note: t("medication_factsheet.interaction_severity_note"),
                      caution: t(
                        "medication_factsheet.interaction_severity_caution",
                      ),
                      critical: t(
                        "medication_factsheet.interaction_severity_critical",
                      ),
                    }}
                  />
                </View>
              ) : null}

              {/* Storage */}
              {storageText ? (
                <View
                  onLayout={registerSectionLayout("storage")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={AlertCircle}>
                    {t("medication_factsheet.section_storage")}
                  </FactsheetSectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.textMid,
                      lineHeight: 21,
                    }}
                  >
                    {storageText}
                  </Text>
                </View>
              ) : null}

              {/* Available as — collapsible. Newer shape (`availability`)
                  groups by brand and shows flags of the markets where it's
                  sold; falls back to `brandsByCountry`. Official
                  documentation links render in the bottom resources section. */}
              {body.availability?.length || body.brandsByCountry?.length ? (
                <View
                  onLayout={registerSectionLayout("availableAs")}
                  style={{ marginTop: 24 }}
                >
                  <Pressable
                    onPress={() => {
                      capturePress(
                        pressLabel(
                          t("medication_factsheet.section_available_as"),
                        ),
                        brandsExpanded
                          ? "collapse_available_as"
                          : "expand_available_as",
                      );
                      setBrandsExpanded((current) => !current);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`${
                      brandsExpanded ? "Collapse" : "Expand"
                    } ${t("medication_factsheet.section_available_as")}`}
                    style={({ pressed }) => ({
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity: pressed ? 0.72 : 1,
                    })}
                  >
                    <SectionTitle icon={Globe}>
                      {t("medication_factsheet.section_available_as")}
                    </SectionTitle>
                    {brandsExpanded ? (
                      <ChevronUp
                        size={16}
                        color={EMBER.colors.textLight}
                        strokeWidth={2}
                      />
                    ) : (
                      <ChevronDown
                        size={16}
                        color={EMBER.colors.textLight}
                        strokeWidth={2}
                      />
                    )}
                  </Pressable>
                  <Collapsible expanded={brandsExpanded}>
                    <View style={{ paddingTop: 10 }}>
                      {body.availability?.length
                        ? body.availability.map((brand, i) => {
                            return (
                              <View key={i} style={{ marginBottom: 12 }}>
                                <Text
                                  className="font-dm-sans"
                                  style={{
                                    fontSize: 14,
                                    fontWeight: "600",
                                    color: EMBER.colors.textMid,
                                    marginBottom: 4,
                                  }}
                                >
                                  {brand.name}
                                  {brand.manufacturer ? (
                                    <Text
                                      className="font-dm-sans"
                                      style={{
                                        fontWeight: "400",
                                        color: EMBER.colors.textLight,
                                      }}
                                    >
                                      {`  ·  ${brand.manufacturer}`}
                                    </Text>
                                  ) : null}
                                </Text>
                                <Text style={{ fontSize: 18, lineHeight: 26 }}>
                                  {brand.countries
                                    .map(flagFromIso)
                                    .filter(Boolean)
                                    .join(" ")}
                                </Text>
                              </View>
                            );
                          })
                        : body.brandsByCountry?.map((entry, i) => {
                            return (
                              <View key={i} style={{ marginBottom: 10 }}>
                                <Text
                                  className="font-dm-sans"
                                  style={{
                                    fontSize: 13,
                                    fontWeight: "600",
                                    color: EMBER.colors.textMid,
                                    marginBottom: 4,
                                  }}
                                >
                                  {entry.country}
                                </Text>
                                <View
                                  style={{
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 6,
                                  }}
                                >
                                  {entry.brands.map((brand) => (
                                    <View
                                      key={brand}
                                      style={{
                                        backgroundColor: "rgba(0,0,0,0.05)",
                                        borderRadius: 8,
                                        paddingHorizontal: 10,
                                        paddingVertical: 5,
                                      }}
                                    >
                                      <Text
                                        className="font-dm-sans"
                                        style={{
                                          fontSize: 13,
                                          color: EMBER.colors.textLight,
                                        }}
                                      >
                                        {brand}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </View>
                            );
                          })}
                    </View>
                  </Collapsible>
                </View>
              ) : null}

              {/* Official Resources */}
              {officialResources.length ? (
                <View
                  onLayout={registerSectionLayout("resources")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={BookOpen}>
                    {t("medication_factsheet.section_official_resources")}
                  </FactsheetSectionTitle>
                  {officialResources.map((res) => (
                    <Pressable
                      key={res.url}
                      onPress={() => {
                        // Resource labels are authored catalogue copy.
                        capturePress(
                          pressLabel(res.label),
                          "official_resource_link",
                        );
                        Linking.openURL(res.url);
                      }}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                        paddingVertical: 10,
                      }}
                    >
                      <View
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          backgroundColor:
                            res.type === "video"
                              ? "rgba(184, 74, 22, 0.1)"
                              : "rgba(0,0,0,0.04)",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Text style={{ fontSize: 13 }}>
                          {res.type === "video"
                            ? "\u25B6"
                            : res.type === "guide"
                              ? "\uD83D\uDCC4"
                              : "\u2139\uFE0F"}
                        </Text>
                      </View>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          fontWeight: "500",
                          color: EMBER.colors.textMid,
                          flex: 1,
                        }}
                      >
                        {res.label}
                      </Text>
                      <ExternalLink
                        size={13}
                        color={EMBER.colors.textFaint}
                        strokeWidth={1.5}
                      />
                    </Pressable>
                  ))}
                </View>
              ) : null}

              {/* Evidence sources */}
              {item.sources?.length ? (
                <View
                  onLayout={registerSectionLayout("evidence")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={BookOpen}>
                    {t("medication_factsheet.section_evidence")}
                  </FactsheetSectionTitle>
                  {item.sources.map((source, i) => (
                    <View key={i} style={{ gap: 8, marginBottom: 16 }}>
                      {source.evidenceLevel &&
                        EVIDENCE_LEVELS[source.evidenceLevel] && (
                          <EvidenceLevelChip
                            level={source.evidenceLevel}
                            onPress={() =>
                              setEvidenceModal(source.evidenceLevel!)
                            }
                          />
                        )}
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          color: EMBER.colors.textMid,
                          lineHeight: 20,
                        }}
                      >
                        {source.title}
                        {source.journal ? ` \u2014 ${source.journal}` : ""}
                        {source.year ? ` (${source.year})` : ""}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}

              {/* User context: History timeline (from userContext prop) */}
              {history?.length ? (
                <View
                  onLayout={registerSectionLayout("history")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={History}>
                    {t("medication_factsheet.section_your_history")}
                  </FactsheetSectionTitle>
                  {history.map((h, i) => {
                    const isCurrent = i === history.length - 1;
                    return (
                      <View
                        key={h.cycle}
                        style={{
                          flexDirection: "row",
                          gap: 12,
                          paddingVertical: 12,
                        }}
                      >
                        <View style={{ alignItems: "center", paddingTop: 4 }}>
                          <View
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: isCurrent
                                ? EMBER.colors.warm
                                : "rgba(0,0,0,0.08)",
                              borderWidth: isCurrent ? 0 : 1.5,
                              borderColor: "rgba(0,0,0,0.12)",
                            }}
                          />
                        </View>
                        <View style={{ flex: 1 }}>
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 14,
                                fontWeight: "600",
                                color: isCurrent
                                  ? EMBER.colors.text
                                  : EMBER.colors.textMid,
                              }}
                            >
                              {h.cycle}
                              {isCurrent ? " (current)" : ""}
                            </Text>
                            <View
                              style={{
                                paddingHorizontal: 8,
                                paddingVertical: 2,
                                borderRadius: 6,
                                backgroundColor: "rgba(0,0,0,0.04)",
                              }}
                            >
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 11,
                                  fontWeight: "600",
                                  color: EMBER.colors.textLight,
                                }}
                              >
                                {h.dosage}
                              </Text>
                            </View>
                          </View>
                          {h.outcome && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 13,
                                color: EMBER.colors.green ?? "#4E7342",
                                fontWeight: "500",
                                marginTop: 4,
                              }}
                            >
                              {h.outcome}
                            </Text>
                          )}
                          {h.note && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 13,
                                color: EMBER.colors.textLight,
                                lineHeight: 19,
                                marginTop: 3,
                              }}
                            >
                              {h.note}
                            </Text>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              ) : null}

              {/* User context: Prescription (from userContext prop) */}
              {prescription ? (
                <View
                  onLayout={registerSectionLayout("prescription")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={FileText}>
                    {t("medication_factsheet.section_prescription")}
                  </FactsheetSectionTitle>
                  <View
                    style={{
                      backgroundColor: "rgba(0,0,0,0.02)",
                      borderRadius: 12,
                      padding: 14,
                      gap: 10,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 13, color: EMBER.colors.textLight }}
                      >
                        {t("medication_factsheet.row_prescriber")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          fontWeight: "600",
                          color: EMBER.colors.text,
                        }}
                      >
                        {prescription.prescriber}
                      </Text>
                    </View>
                    {prescription.pharmacy && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_pharmacy")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {prescription.pharmacy}
                        </Text>
                      </View>
                    )}
                    {prescription.rxNumber && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_rx_number")}
                        </Text>
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Hash
                            size={11}
                            color={EMBER.colors.textLight}
                            strokeWidth={1.5}
                          />
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 13,
                              fontWeight: "600",
                              color: EMBER.colors.text,
                              fontVariant: ["tabular-nums"],
                            }}
                          >
                            {prescription.rxNumber}
                          </Text>
                        </View>
                      </View>
                    )}
                    {prescription.quantity && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_quantity")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {prescription.quantity}
                        </Text>
                      </View>
                    )}
                    {prescription.prescribedDate && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_prescribed")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {prescription.prescribedDate}
                        </Text>
                      </View>
                    )}
                    {prescription.refillsRemaining !== undefined && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_refills_remaining")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color:
                              prescription.refillsRemaining === 0
                                ? "#B84A16"
                                : EMBER.colors.text,
                          }}
                        >
                          {prescription.refillsRemaining === 0
                            ? t("medication_factsheet.refills_none")
                            : prescription.refillsRemaining}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              ) : null}

              {/* Disclaimer */}
              <View
                style={{
                  marginTop: 28,
                  paddingTop: 20,
                  borderTopWidth: 1,
                  borderTopColor: "rgba(0,0,0,0.05)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                    lineHeight: 18,
                  }}
                >
                  {t("medication_factsheet.disclaimer")}
                </Text>
              </View>
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>

      <LinkedContentSheet
        item={linkedItem}
        onClose={() => setLinkedItem(null)}
      />

      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />

      {deleteConfirmVisible && (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={() => setDeleteConfirmVisible(false)}
        >
          <Pressable
            onPress={() => {
              capturePress("dismiss_delete_medication_backdrop");
              if (!deleteMedication.isPending) setDeleteConfirmVisible(false);
            }}
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.45)",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 24,
            }}
          >
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 360,
                borderRadius: 20,
                backgroundColor: "#fff",
                padding: 22,
                gap: 14,
                shadowColor: "#000",
                shadowOpacity: 0.15,
                shadowRadius: 20,
                shadowOffset: { width: 0, height: 8 },
                elevation: 8,
              }}
            >
              <View
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: "rgba(220, 38, 38, 0.1)",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "center",
                }}
              >
                <Trash2 size={20} color="#DC2626" strokeWidth={2} />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 17,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  textAlign: "center",
                }}
              >
                {t("medication_factsheet.delete_modal_title", {
                  params: { name: userContext?.originalName ?? item.title },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                  lineHeight: 20,
                }}
              >
                {t("medication_factsheet.delete_modal_message")}
              </Text>
              <View style={{ flexDirection: "row", gap: 10, marginTop: 4 }}>
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("medication_factsheet.delete_modal_cancel")),
                      "medication_factsheet.delete_modal_cancel",
                    );
                    setDeleteConfirmVisible(false);
                  }}
                  disabled={deleteMedication.isPending}
                  style={{
                    flex: 1,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: "rgba(0,0,0,0.04)",
                    alignItems: "center",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "600",
                      color: EMBER.colors.textMid,
                    }}
                  >
                    {t("medication_factsheet.delete_modal_cancel")}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        t("medication_factsheet.delete_modal_confirm"),
                      ),
                      "medication_factsheet.delete_modal_confirm",
                    );
                    handleConfirmDelete();
                  }}
                  disabled={deleteMedication.isPending}
                  style={{
                    flex: 1,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: "#DC2626",
                    alignItems: "center",
                    opacity: deleteMedication.isPending ? 0.6 : 1,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "600",
                      color: "#fff",
                    }}
                  >
                    {deleteMedication.isPending
                      ? t("medication_factsheet.delete_modal_deleting")
                      : t("medication_factsheet.delete_modal_confirm")}
                  </Text>
                </Pressable>
              </View>
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </View>
  );
}

export default function MedicationFactsheetSheet({
  item,
  onClose,
  userContext,
  medicationId,
  onLogDosePress,
  onEditPress,
  fallbackName,
  marketContext,
}: Props) {
  if (!item && !fallbackName) return null;
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      {item ? (
        <MedicationDetailContent
          item={item}
          onClose={onClose}
          userContext={userContext}
          onEditPress={onEditPress}
          medicationId={medicationId}
          onLogDosePress={onLogDosePress}
          marketContext={marketContext}
        />
      ) : (
        <MedicationMinimalContent
          name={fallbackName ?? ""}
          userContext={userContext}
          medicationId={medicationId}
          onClose={onClose}
          onLogDosePress={onLogDosePress}
          onEditPress={onEditPress}
        />
      )}
    </Modal>
  );
}

function MedicationMinimalContent({
  name,
  userContext,
  medicationId,
  onClose,
  onLogDosePress,
  onEditPress,
}: {
  name: string;
  userContext?: MedicationUserContext;
  medicationId?: string;
  onClose: () => void;
  onLogDosePress?: () => void;
  onEditPress?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationMinimalContent");
  const { format: fmtDate } = useDateFormat();
  const updateMedication = useUpdateMedication();
  const deleteMedication = useDeleteMedication();
  const dosage = userContext?.dosage ?? null;
  const route = userContext?.route ? formatRoute(userContext.route, t) : null;
  const startDate = userContext?.startDate
    ? fmtDate(userContext.startDate)
    : null;
  const endDate = userContext?.endDate ? fmtDate(userContext.endDate) : null;
  const notes = userContext?.notes ?? null;
  const isStopped = isMedicationStopped(userContext?.notes);
  const isMutating = updateMedication.isPending || deleteMedication.isPending;
  const providerOwned = isProviderOwnedMedication(userContext?.source);
  const canStop = !!medicationId && !isStopped && !providerOwned;
  const canEdit = !!medicationId && !!onEditPress && !providerOwned;
  const canDelete = !!medicationId && !providerOwned;
  const showActions = canStop || canEdit;

  // Inline confirm UI for Stop / Delete — Alert.alert isn't reliable on web.
  const [confirmMode, setConfirmMode] = useState<"view" | "stop" | "delete">(
    "view",
  );
  const [stopReason, setStopReason] = useState("");
  const [mutationError, setMutationError] = useState<string | null>(null);

  const openStopConfirm = useCallback(() => {
    setStopReason("");
    setMutationError(null);
    setConfirmMode("stop");
  }, []);

  const handleConfirmStop = useCallback(() => {
    if (!medicationId) {
      setMutationError(t("medication_factsheet.missing_med_id"));
      return;
    }
    setMutationError(null);
    const todayISO = getTodayString();
    const reason = stopReason.trim();
    updateMedication.mutate(
      {
        medicationId,
        endDate: todayISO,
        notes: prependStoppedMedicationNote(userContext?.notes, reason),
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          console.error("[MedicationMinimalFactsheet] Stop failed", err);
          setMutationError(
            err instanceof Error
              ? err.message
              : t("medication_factsheet.stop_med_error"),
          );
        },
      },
    );
  }, [
    medicationId,
    userContext?.notes,
    stopReason,
    updateMedication,
    onClose,
    t,
  ]);

  const handleConfirmDelete = useCallback(() => {
    if (!medicationId) {
      setMutationError(t("medication_factsheet.delete_med_error"));
      return;
    }
    setMutationError(null);
    deleteMedication.mutate(medicationId, {
      onSuccess: () => onClose(),
      onError: (err) => {
        console.error("[MedicationMinimalFactsheet] Delete failed", err);
        setMutationError(
          err instanceof Error
            ? err.message
            : t("medication_factsheet.delete_med_error"),
        );
      },
    });
  }, [medicationId, deleteMedication, onClose, t]);

  return (
    <Pressable
      onPress={() => {
        capturePress("dismiss_medication_sheet_backdrop");
        onClose();
      }}
      disabled={isMutating}
      style={{
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
      }}
    >
      <Pressable
        onPress={(e) => e.stopPropagation()}
        style={{
          backgroundColor: EMBER.colors.surface,
          borderRadius: 20,
          width: "100%",
          maxWidth: 460,
          maxHeight: "90%",
          overflow: "hidden",
        }}
      >
        <ScrollView
          contentContainerStyle={{ paddingHorizontal: 20, paddingVertical: 18 }}
          showsVerticalScrollIndicator={false}
        >
          {/* Close */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <IconButton
              onPress={onClose}
              disabled={isMutating}
              hitSlop={8}
              size={28}
              surface="soft"
              backgroundColor="rgba(0, 0, 0, 0.05)"
              pressedBackgroundColor="rgba(0, 0, 0, 0.05)"
              pressedOpacity={1}
              accessibilityLabel="Close"
            >
              <X size={14} color={EMBER.colors.textLight} />
            </IconButton>
          </View>

          {/* Title */}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 24,
              fontWeight: "700",
              color: EMBER.colors.text,
              lineHeight: 30,
              marginBottom: 4,
              textAlign: "center",
            }}
          >
            {name}
          </Text>

          {/* No-factsheet advisory */}
          <View
            style={{
              flexDirection: "row",
              gap: 10,
              padding: 12,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: EMBER.colors.border,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              marginTop: 16,
              marginBottom: 8,
            }}
          >
            <AlertCircle
              size={14}
              color={EMBER.colors.textMid}
              strokeWidth={1.5}
              style={{ marginTop: 2 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 13,
                color: EMBER.colors.textMid,
                lineHeight: 18,
              }}
            >
              {t("medication_factsheet.minimal_no_factsheet")}
            </Text>
          </View>

          {/* Patient context */}
          <View
            style={{
              backgroundColor: `${EMBER.colors.warm}0A`,
              borderRadius: 12,
              padding: 16,
              marginTop: 8,
              gap: 10,
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <Pill size={14} color={EMBER.colors.warm} strokeWidth={2} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "700",
                  color: EMBER.colors.warm,
                }}
              >
                {t("medication_factsheet.your_medication_fallback")}
              </Text>
            </View>
            {dosage ? (
              <InfoRow
                label={t("medication_factsheet.row_dosage")}
                value={dosage}
              />
            ) : null}
            {route ? (
              <InfoRow
                label={t("medication_factsheet.row_route")}
                value={route}
              />
            ) : null}
            {startDate ? (
              <InfoRow
                label={t("medication_factsheet.row_start_date")}
                value={startDate}
              />
            ) : null}
            {endDate ? (
              <InfoRow
                label={t("medication_factsheet.row_end_date")}
                value={endDate}
              />
            ) : null}
            {notes ? (
              <InfoRow
                label={t("medication_factsheet.row_notes")}
                value={notes}
              />
            ) : null}
            {!dosage && !route && !startDate && !endDate && !notes ? (
              <Text
                className="font-dm-sans"
                style={{ fontSize: 13, color: EMBER.colors.textLight }}
              >
                {t("medication_factsheet.minimal_no_details")}
              </Text>
            ) : null}
          </View>

          {/* Actions */}
          {confirmMode !== "view" ? (
            <View style={{ marginTop: 24, gap: 10 }}>
              <View
                style={{
                  padding: 14,
                  borderRadius: 12,
                  backgroundColor:
                    confirmMode === "delete"
                      ? "rgba(220, 38, 38, 0.04)"
                      : "rgba(234, 88, 12, 0.04)",
                  borderWidth: 1,
                  borderColor:
                    confirmMode === "delete"
                      ? "rgba(220, 38, 38, 0.15)"
                      : "rgba(234, 88, 12, 0.15)",
                  gap: 12,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {confirmMode === "delete"
                    ? t("medication_factsheet.minimal_delete_title", {
                        params: { name },
                      })
                    : t("medication_factsheet.minimal_stop_title")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textMid,
                    lineHeight: 18,
                  }}
                >
                  {confirmMode === "delete"
                    ? t("medication_factsheet.minimal_delete_help")
                    : t("medication_factsheet.minimal_stop_help")}
                </Text>
                {confirmMode === "stop" && (
                  <TextInput
                    value={stopReason}
                    onChangeText={setStopReason}
                    placeholder={t(
                      "medication_factsheet.minimal_stop_placeholder",
                    )}
                    placeholderTextColor={EMBER.colors.textFaint}
                    multiline
                    style={{
                      minHeight: 64,
                      maxHeight: 160,
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: EMBER.colors.border,
                      paddingVertical: 10,
                      paddingHorizontal: 12,
                      backgroundColor: "#FFFFFF",
                      fontSize: 14,
                      color: EMBER.colors.text,
                      fontFamily: "DMSans_400Regular",
                      ...(Platform.OS === "web"
                        ? { outlineStyle: "none" as const }
                        : {}),
                    }}
                  />
                )}
                {mutationError ? (
                  <View
                    style={{
                      backgroundColor: "rgba(220, 38, 38, 0.08)",
                      borderWidth: 1,
                      borderColor: "rgba(220, 38, 38, 0.2)",
                      borderRadius: 8,
                      padding: 10,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: "#991B1B",
                        lineHeight: 18,
                      }}
                    >
                      {mutationError}
                    </Text>
                  </View>
                ) : null}
                <View style={{ flexDirection: "row", gap: 8 }}>
                  <Pressable
                    onPress={() => {
                      capturePress(
                        pressLabel(t("medication_factsheet.stop_form_cancel")),
                        "medication_factsheet.stop_form_cancel",
                      );
                      setMutationError(null);
                      setConfirmMode("view");
                    }}
                    disabled={isMutating}
                    style={{
                      flex: 1,
                      paddingVertical: 11,
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: EMBER.colors.border,
                      alignItems: "center",
                      backgroundColor: "rgba(255,255,255,0.6)",
                      opacity: isMutating ? 0.5 : 1,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: EMBER.colors.textMid,
                      }}
                    >
                      {t("medication_factsheet.stop_form_cancel")}
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      if (confirmMode === "delete") {
                        capturePress(
                          pressLabel(t("medication_factsheet.action_delete")),
                          "medication_factsheet.action_delete",
                        );
                        handleConfirmDelete();
                        return;
                      }
                      capturePress(
                        pressLabel(t("medication_factsheet.action_stop")),
                        "medication_factsheet.action_stop",
                      );
                      handleConfirmStop();
                    }}
                    disabled={isMutating}
                    style={{
                      flex: 1,
                      paddingVertical: 11,
                      borderRadius: 10,
                      alignItems: "center",
                      backgroundColor:
                        confirmMode === "delete" ? "#DC2626" : "#EA580C",
                      opacity: isMutating ? 0.5 : 1,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: "#FFFFFF",
                      }}
                    >
                      {isMutating
                        ? confirmMode === "delete"
                          ? t("medication_factsheet.delete_modal_deleting")
                          : t("medication_factsheet.stop_form_submitting")
                        : confirmMode === "delete"
                          ? t("medication_factsheet.action_delete")
                          : t("medication_factsheet.action_stop")}
                    </Text>
                  </Pressable>
                </View>
                {canDelete && confirmMode === "stop" ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityHint={t(
                      "medication_factsheet.action_delete_help",
                    )}
                    onPress={() => {
                      setMutationError(null);
                      setConfirmMode("delete");
                    }}
                    disabled={isMutating}
                    style={({ pressed }) => ({
                      alignSelf: "flex-start",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                      opacity: isMutating ? 0.5 : pressed ? 0.72 : 1,
                    })}
                  >
                    <Trash2 size={13} color="#DC2626" strokeWidth={1.5} />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: "600",
                        color: "#B91C1C",
                      }}
                    >
                      {t("medication_factsheet.delete_mistake_action")}
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          ) : showActions || providerOwned ? (
            <View style={{ marginTop: 24, gap: 8 }}>
              {providerOwned ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 12,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{ fontSize: 14, fontWeight: "600" }}
                    >
                      {t("medication_factsheet.provider_owned_title")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}
                    >
                      {t("medication_factsheet.provider_owned_note")}
                    </Text>
                  </View>
                </View>
              ) : null}
              {onLogDosePress && medicationId ? (
                <Pressable
                  onPress={onLogDosePress}
                  disabled={isMutating}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 12,
                    opacity: isMutating ? 0.5 : 1,
                  }}
                >
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      backgroundColor: "rgba(184, 74, 22, 0.08)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ClipboardList size={14} color={COPPER} strokeWidth={1.5} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                      }}
                    >
                      {t("medication_factsheet.action_log_dose")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: EMBER.colors.textLight,
                        marginTop: 1,
                      }}
                    >
                      {t("medication_factsheet.action_log_dose_help")}
                    </Text>
                  </View>
                  <ChevronRight
                    size={14}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                </Pressable>
              ) : null}

              {canEdit ? (
                <Pressable
                  onPress={onEditPress}
                  disabled={isMutating}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 12,
                    opacity: isMutating ? 0.5 : 1,
                  }}
                >
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      backgroundColor: "rgba(184, 74, 22, 0.08)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Pencil size={14} color={COPPER} strokeWidth={1.5} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                      }}
                    >
                      {t("medication_factsheet.action_edit")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: EMBER.colors.textLight,
                        marginTop: 1,
                      }}
                    >
                      {t("medication_factsheet.action_edit_help")}
                    </Text>
                  </View>
                  <ChevronRight
                    size={14}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                </Pressable>
              ) : null}

              {canStop ? (
                <Pressable
                  onPress={openStopConfirm}
                  disabled={isMutating}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 12,
                    opacity: isMutating ? 0.5 : 1,
                  }}
                >
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      backgroundColor: "rgba(234, 88, 12, 0.08)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShieldAlert size={14} color="#EA580C" strokeWidth={1.5} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: "#EA580C",
                      }}
                    >
                      {updateMedication.isPending
                        ? t("medication_factsheet.stop_form_submitting")
                        : t("medication_factsheet.action_stop")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: EMBER.colors.textLight,
                        marginTop: 1,
                      }}
                    >
                      {t("medication_factsheet.action_stop_help")}
                    </Text>
                  </View>
                  <ChevronRight
                    size={14}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                </Pressable>
              ) : null}
            </View>
          ) : null}
        </ScrollView>
      </Pressable>
    </Pressable>
  );
}
